import { AbstractDestroyable, Callback1, Destructor, VoidCallback1 } from '@deqo/foundation';
import produce from 'immer';
import { injectable } from 'inversify';
import { pathObserver, Store, StoreObserver } from './Store';

@injectable()
export abstract class ReadStore<T> extends AbstractDestroyable implements Store<T> {

    protected observers = [] as StoreObserver<T>[];
    protected abstract state: T;
    protected hooks = [] as VoidCallback1<T>[];

    constructor() {
        super();
        this.burden(() => this.observers = []);
        this.useHooks();
    }

    public getState(): T {
        return this.state;
    }

    public observe(observer: StoreObserver<T>): Destructor;
    public observe<P>(path: Callback1<P, T>, observer: StoreObserver<P>): Destructor;

    public observe(a: any, b?: any): Destructor {
        if (b) return this.observe(pathObserver(a, b));
        this.observers.push(a);
        return () => this.unobserve(a);
    }

    protected unobserve(observer: StoreObserver<T>) {
        const index = this.observers.indexOf(observer);
        if (index >= 0) {
            this.observers.splice(index, 1);
        }
    }

    protected useHooks() {
    }

    protected hook(h: VoidCallback1<T>) {
        this.hooks.push(h);
    }

    protected mutate(mutator: VoidCallback1<T>): T;
    protected mutate(update: Partial<T>): T;
    protected mutate(input: any) {
        if (typeof (input) !== 'function') {
            return this.mutate(next => Object.assign(next, input));
        }
        return this.setStateAndNotify(
            produce(this.state, draft => {
                input(draft as any);
                this.applyHooks(draft);
            })
        );
    }

    protected mutateTo(newState: T) {
        return this.setStateAndNotify(
            produce(this.state, () => {
                this.applyHooks(newState);
                return newState;
            })
        );
    }

    protected repair(mutator: VoidCallback1<T>) {
        this.state = produce(this.state, draft => {
            mutator(draft as any);
            this.applyHooks(draft);
        });
    }

    private applyHooks(toState: any) {
        this.hooks.forEach(h => h(toState));
        return toState;
    }

    private setStateAndNotify(newState: any) {
        const oldState = this.state;
        this.state = newState;
        if ('debugName' in this) {
            // logStateDiff((this as any).debugName, newState, oldState);
        }
        this.observers.forEach(x => x(newState, oldState));
        return oldState;
    }
}

export function fromState(): PropertyDecorator {
    return (_target: any, name: string | symbol): PropertyDescriptor => {
        const override = {} as PropertyDescriptor;
        override.get = function (this: any) {
            return this.state[name];
        };
        return override;
    };
}
